import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MessageService } from "@openng/optimus-ui/api";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { first } from "rxjs";

import { PERMISSION } from "../../../../enums/permissions.enum";
import {
  ButlerV1HardwareAdapterConfigurationDto,
  ButlerV1HardwareConfigurationPayload,
  HardwareAdapterConfigurationDto,
  HardwareAdapterDirection,
  HardwareAdapterFixedInputOperation,
  HardwareAdapterOutputIndex,
  HardwareAdapterInputIndex,
  HardwareAdapterOperation,
  HardwareAdapterOutputOperation,
  HardwareConfigurationPayload,
} from "../../../../models/api/hardwareAdapterConfigurationDto";
import { HardwareAdapterService } from "../../../../services/api/hardware-adapter/hardware-adapter.service";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import {
  createButlerV1ConfigurationForm,
  HardwareConfigurationOption,
  HardwareConfigurationRow,
  isButlerV1Configuration,
  SupportedHardwareConfigurationForm,
  SupportedHardwareConfigurationRow,
  toOpaqueHardwareConfigurationPayload,
} from "./hardware-configuration-form";
import { HardwareConfigurationRowComponent } from "./hardware-configuration-row/hardware-configuration-row.component";
import { HARDWARE_ADAPTER_TYPES, HardwareAdapterType } from "../../../../enums/hardwareAdapterTypes";

interface HardwareConfigurationDialogData {
  gateSystemId?: string | null;
}

interface DuplicateCustomSignalLabelError {
  rowNumbers: number[];
}

interface ConfigurationValidationValue {
  operation: unknown;
  direction: unknown;
  index: unknown;
  label: unknown;
  rowNumber: number;
}

@Component({
  selector: "app-hardware-configuration-dialog",
  standalone: true,
  imports: [
    ButtonComponent,
    HardwareConfigurationRowComponent,
    HasPermissionDirective,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TimeoutComponent,
    TranslateModule,
  ],
  templateUrl: "./hardware-configuration-dialog.component.html",
  styleUrl: "./hardware-configuration-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HardwareConfigurationDialogComponent implements OnInit {
  private readonly config = inject<DynamicDialogConfig<HardwareConfigurationDialogData>>(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly hardwareAdapterService = inject(HardwareAdapterService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  protected readonly permissions = inject(PermissionService);
  private readonly gateSystemId = this.config.data?.gateSystemId;
  protected readonly hasGateSystemId = Boolean(this.gateSystemId);
  protected readonly canUpdateHardwareConfiguration = this.permissions.hasPermission(PERMISSION.HARDWARE_CONFIG_UPDATE);

  protected readonly loading = signal(true);
  protected readonly loadingTimeout = createTimeoutSignal(this.loading);
  protected readonly saving = signal(false);
  protected readonly rows = signal<HardwareConfigurationRow[]>([]);
  private nextRowId = 0;

  protected readonly operationOptions: HardwareConfigurationOption<HardwareAdapterOperation>[] = [
    { label: "gate_administration.hardware_configuration.operations.open", value: "door.open" },
    { label: "gate_administration.hardware_configuration.operations.close", value: "door.close" },
    { label: "gate_administration.hardware_configuration.operations.stop", value: "door.stop" },
    { label: "gate_administration.hardware_configuration.operations.opened", value: "door.opened" },
    { label: "gate_administration.hardware_configuration.operations.closed", value: "door.closed" },
    {
      label: "gate_administration.hardware_configuration.operations.custom_signal",
      value: "door.customSignal",
    },
  ];
  protected readonly adapterTypeOptions: HardwareConfigurationOption<HardwareAdapterType>[] = [
    {
      label: "gate_administration.hardware_configuration.hardware_types.butler_v1",
      value: HARDWARE_ADAPTER_TYPES.BUTLER_V1,
    },
  ];
  protected readonly indexOptions: HardwareConfigurationOption<HardwareAdapterOutputIndex>[] = [1, 2, 3, 4, 5].map(
    (index) => ({
      label: index.toString(),
      value: index as HardwareAdapterOutputIndex,
    })
  );

  protected readonly form = new FormGroup({
    configurations: new FormArray<SupportedHardwareConfigurationForm>([], {
      validators: [this.uniqueConfigurationValidator()],
    }),
  });

  public ngOnInit(): void {
    if (!this.gateSystemId) {
      this.loading.set(false);
      return;
    }

    this.hardwareAdapterService
      .getHardwareConfiguration(this.gateSystemId)
      .pipe(first())
      .subscribe({
        next: (configuration) => {
          this.replaceConfigurationRows(configuration);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  protected addConfigurationRow(): void {
    if (!this.canUpdateHardwareConfiguration || !this.gateSystemId) {
      return;
    }

    const row = this.createSupportedRow();
    this.form.controls.configurations.push(row.form);
    row.form.markAsDirty();
    this.rows.update((rows) => [...rows, row]);
    this.form.controls.configurations.updateValueAndValidity({ emitEvent: false });
  }

  protected removeConfigurationRow(rowId: string): void {
    if (!this.canUpdateHardwareConfiguration) {
      return;
    }

    const row = this.rows().find((candidate) => candidate.id === rowId);
    if (!row) {
      return;
    }

    if (row.kind === "supported") {
      const formIndex = this.form.controls.configurations.controls.indexOf(row.form);
      if (formIndex >= 0) {
        this.form.controls.configurations.removeAt(formIndex);
      }
    }

    this.rows.update((rows) => rows.filter((candidate) => candidate.id !== rowId));
    this.form.controls.configurations.updateValueAndValidity({ emitEvent: false });
  }

  protected getOperationOptions(
    currentRow: SupportedHardwareConfigurationRow
  ): HardwareConfigurationOption<HardwareAdapterOperation>[] {
    const direction = currentRow.form.controls.configuration.controls.direction.value;
    if (direction === null) {
      return [];
    }

    return this.operationOptions
      .filter((option) => this.operationMatchesDirection(option.value, direction))
      .map((option) => ({
        ...option,
        disabled:
          option.value !== "door.customSignal" &&
          this.rows().some((row) => row.id !== currentRow.id && this.getRowOperation(row) === option.value),
      }));
  }

  protected getIndexOptions(
    currentRow: SupportedHardwareConfigurationRow
  ): HardwareConfigurationOption<HardwareAdapterOutputIndex>[] {
    const direction = currentRow.form.controls.configuration.controls.direction.value;
    if (direction === null) {
      return [];
    }

    return this.indexOptions
      .filter((option) => direction === "output" || option.value <= 3)
      .map((option) => ({
        ...option,
        disabled:
          direction !== null &&
          this.rows().some(
            (row) =>
              row.kind === "supported" &&
              row.id !== currentRow.id &&
              row.form.controls.configuration.controls.direction.value === direction &&
              row.form.controls.configuration.controls.index.value === option.value
          ),
      }));
  }

  protected saveConfiguration(): void {
    if (!this.canUpdateHardwareConfiguration || !this.gateSystemId) {
      return;
    }

    const payload = this.buildPayload();
    if (!payload || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.hardwareAdapterService
      .editHardwareConfiguration(this.gateSystemId, payload)
      .pipe(first())
      .subscribe({
        next: (configuration) => {
          this.replaceConfigurationRows(configuration);
          this.saving.set(false);
          this.messageService.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant("gate_administration.hardware_configuration.save_success"),
            life: 4000,
          });
        },
        error: () => {
          this.saving.set(false);
        },
      });
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected getDuplicateCustomSignalLabelRows(): string {
    const error = this.form.controls.configurations.getError(
      "duplicateCustomSignalLabel"
    ) as DuplicateCustomSignalLabelError | null;
    return error?.rowNumbers.join(", ") ?? "";
  }

  private createSupportedRow(
    configuration?: ButlerV1HardwareAdapterConfigurationDto
  ): SupportedHardwareConfigurationRow {
    return {
      kind: "supported",
      id: this.createRowId(),
      form: createButlerV1ConfigurationForm(configuration),
    };
  }

  private replaceConfigurationRows(configuration: HardwareAdapterConfigurationDto[]): void {
    this.form.controls.configurations.clear();

    const rows = configuration.map<HardwareConfigurationRow>((configurationEntry) => {
      if (isButlerV1Configuration(configurationEntry)) {
        const row = this.createSupportedRow(configurationEntry);
        this.form.controls.configurations.push(row.form);
        return row;
      }

      return {
        kind: "unsupported",
        id: this.createRowId(),
        adapterType: configurationEntry.adapterType,
        payload: toOpaqueHardwareConfigurationPayload(configurationEntry),
        validity: configurationEntry.validity,
      };
    });

    this.rows.set(rows);
    this.form.controls.configurations.updateValueAndValidity({ emitEvent: false });

    if (!this.canUpdateHardwareConfiguration) {
      this.form.disable({ emitEvent: false });
    }

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private buildPayload(): HardwareConfigurationPayload[] | null {
    const payload: HardwareConfigurationPayload[] = [];

    for (const row of this.rows()) {
      if (row.kind === "unsupported") {
        payload.push(row.payload);
        continue;
      }

      const formValue = row.form.getRawValue();
      const { operation, direction, index, label, signalId } = formValue.configuration;
      if (operation === null || direction === null || index === null) {
        return null;
      }

      const butlerV1Payload = this.buildButlerV1Payload({ operation, direction, index, label, signalId });
      if (!butlerV1Payload) {
        return null;
      }
      payload.push(butlerV1Payload);
    }

    return payload;
  }

  private uniqueConfigurationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormArray)) {
        return null;
      }

      const configurations = control.controls.map<ConfigurationValidationValue>((row, rowIndex) => {
        const visualRowIndex = this.rows().findIndex(
          (candidate) => candidate.kind === "supported" && candidate.form === row
        );
        return {
          operation: row.get(["configuration", "operation"])?.value as unknown,
          direction: row.get(["configuration", "direction"])?.value as unknown,
          index: row.get(["configuration", "index"])?.value as unknown,
          label: row.get(["configuration", "label"])?.value as unknown,
          rowNumber: visualRowIndex >= 0 ? visualRowIndex + 1 : rowIndex + 1,
        };
      });
      const operations = configurations.flatMap(({ operation }) =>
        typeof operation === "string" && operation !== "door.customSignal" ? [operation] : []
      );
      const targets = configurations.flatMap(({ direction, index }) =>
        typeof direction === "string" && typeof index === "number" ? [`${direction}:${index}`] : []
      );

      const errors: ValidationErrors = {};
      if (this.hasDuplicates(operations)) {
        errors["duplicateOperation"] = true;
      }
      if (this.hasDuplicates(targets)) {
        errors["duplicateTarget"] = true;
      }
      const duplicateCustomSignalLabelRows = this.getDuplicateCustomSignalLabelRowsFrom(configurations);
      if (duplicateCustomSignalLabelRows.length > 0) {
        errors["duplicateCustomSignalLabel"] = { rowNumbers: duplicateCustomSignalLabelRows };
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private getRowOperation(row: HardwareConfigurationRow): HardwareAdapterOperation | null {
    return row.kind === "supported" ? row.form.controls.configuration.controls.operation.value : row.payload.operation;
  }

  private hasDuplicates(values: string[]): boolean {
    return new Set(values).size !== values.length;
  }

  private getDuplicateCustomSignalLabelRowsFrom(configurations: ConfigurationValidationValue[]): number[] {
    const rowsByLabel = new Map<string, number[]>();
    configurations.forEach(({ operation, label, rowNumber }) => {
      if (operation !== "door.customSignal" || typeof label !== "string" || label.trim().length === 0) {
        return;
      }

      const normalizedLabel = label.trim().toLowerCase();
      rowsByLabel.set(normalizedLabel, [...(rowsByLabel.get(normalizedLabel) ?? []), rowNumber]);
    });

    return [...rowsByLabel.values()]
      .filter((rowNumbers) => rowNumbers.length > 1)
      .flat()
      .sort((left, right) => left - right);
  }

  private buildButlerV1Payload(configuration: {
    operation: HardwareAdapterOperation;
    direction: HardwareAdapterDirection;
    index: HardwareAdapterOutputIndex;
    label: string;
    signalId: string | null;
  }): ButlerV1HardwareConfigurationPayload | null {
    const { operation, direction, index, label, signalId } = configuration;
    if (this.isOutputOperation(operation) && direction === "output") {
      return { operation, adapterType: HARDWARE_ADAPTER_TYPES.BUTLER_V1, config: { direction, index } };
    }

    if (!this.isInputIndex(index) || direction !== "input") {
      return null;
    }

    if (this.isFixedInputOperation(operation)) {
      return { operation, adapterType: HARDWARE_ADAPTER_TYPES.BUTLER_V1, config: { direction, index } };
    }

    const trimmedLabel = label.trim();
    if (operation !== "door.customSignal" || trimmedLabel.length === 0 || trimmedLabel.length > 255) {
      return null;
    }

    return {
      operation,
      adapterType: HARDWARE_ADAPTER_TYPES.BUTLER_V1,
      config: {
        direction,
        index,
        label: trimmedLabel,
        ...(signalId === null ? {} : { signalId }),
      },
    };
  }

  private operationMatchesDirection(operation: HardwareAdapterOperation, direction: HardwareAdapterDirection): boolean {
    return direction === "output" ? this.isOutputOperation(operation) : !this.isOutputOperation(operation);
  }

  private isOutputOperation(operation: HardwareAdapterOperation): operation is HardwareAdapterOutputOperation {
    return operation === "door.open" || operation === "door.close" || operation === "door.stop";
  }

  private isFixedInputOperation(operation: HardwareAdapterOperation): operation is HardwareAdapterFixedInputOperation {
    return operation === "door.opened" || operation === "door.closed";
  }

  private isInputIndex(index: HardwareAdapterOutputIndex): index is HardwareAdapterInputIndex {
    return index === 1 || index === 2 || index === 3;
  }

  private createRowId(): string {
    const rowId = `hardware-configuration-${this.nextRowId}`;
    this.nextRowId += 1;
    return rowId;
  }
}
